import { type RefObject } from "react"

import { useMeasuredTableHeight } from "./use-measured-table-height.ts"
import { useInfiniteScroll } from "./use-table-infinite-scroll.tsx"

interface UseScrolledTableParams {
  /** A page is in flight. */
  loading: boolean
  /** The page the list would load next; absent, the list has ended. */
  next?: string | null
  loadMore: () => void | Promise<void>
  /** The list is read-only for now — a locked project, an in-flight edit. */
  disabled?: boolean
  /**
   * The list owns the page, so the scrollport is the only cap on its height.
   * Left unset the body stops at the shared 600px, which is what a table
   * sharing a page with other sections needs.
   */
  fullPage?: boolean
  /**
   * Size the body for the screen the card gets once scrolled to, below this
   * much sticky chrome — for a page carrying a block above the list that is
   * worth scrolling past. See `useFittedTableHeight`.
   */
  fillsViewportBelow?: number
}

interface ScrolledTable<T extends HTMLElement> {
  /** Goes on the element wrapping both the table and its result count. */
  tableRef: RefObject<T | null>
  /** For the table's `scroll.y`. */
  tableHeight: number
  onTableScroll: ReturnType<typeof useInfiniteScroll>["onTableScroll"]
}

/**
 * The three hooks a scrolled list always wires together, wired once.
 *
 * They are not independent: the footer's height is what the body has to give
 * back, the container the footer is measured in is the one the scroll fallback
 * has to search, and the page the listener guards against asking twice for is
 * the same `next` that decides whether it listens at all. Wiring them by hand
 * is thirteen chances to pass the ref to two of the three, or to reserve a
 * footer nobody measured — and the caller cannot tell, because every mistake
 * degrades quietly into a table that stops at its first page or a count pushed
 * under the fold.
 *
 * Not for a table sharing a scrolling column with another one: the height half
 * fills the space below the body, so the first table would take the whole
 * scrollport. Those keep a fixed height — see the decision in `docs/kb`.
 */
export function useScrolledTable<T extends HTMLElement = HTMLDivElement>({
  loading,
  next = null,
  loadMore,
  disabled,
  fullPage,
  fillsViewportBelow
}: UseScrolledTableParams): ScrolledTable<T> {
  const { tableRef, tableHeight } = useMeasuredTableHeight<T>({
    fullPage,
    fillsViewportBelow
  })

  const { onTableScroll } = useInfiniteScroll<T>({
    loading,
    hasMore: !!next,
    loadMoreKey: next,
    loadMore,
    disabled,
    containerRef: tableRef
  })

  return { tableRef, tableHeight, onTableScroll }
}
